import type { FinancialOverview, FinancialSalesReprojectionResult } from '@tosho/contracts';
export type { FinancialOverview } from '@tosho/contracts';

export const financialOverviewApi = {
  async reprojectSales(file: File, csrfToken: string): Promise<FinancialSalesReprojectionResult> {
    if (!file.size || file.size > 15_728_640 || !csrfToken)
      throw new Error('Export ou session indisponible.');
    const response = await fetch('/api/finance/sales-projections', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-sales+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) throw new Error('Relecture financière impossible.');
    return response.json() as Promise<FinancialSalesReprojectionResult>;
  },
  async readDemoPermission(): Promise<{ enabled: boolean }> {
    const response = await fetch('/api/platform/finance-demo/self', { credentials: 'include' });
    if (!response.ok) throw new Error('Autorisation du mode démo indisponible.');
    return response.json() as Promise<{ enabled: boolean }>;
  },
  async saveExpenseKinds(
    kinds: NonNullable<FinancialOverview['expenseCategoryKinds']>,
    version: string | null,
    csrfToken: string,
  ): Promise<void> {
    const response = await fetch('/api/finance/expense-kinds', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ kinds, version }),
    });
    if (response.status === 409)
      throw new Error('Les réglages ont changé. Fermez puis rouvrez cette fenêtre.');
    if (!response.ok) throw new Error('Les types de dépenses n’ont pas pu être enregistrés.');
  },
  async saveTreasuryColors(
    thresholds: readonly string[],
    csrfToken: string,
    reserveTargetCents?: string,
  ): Promise<void> {
    const response = await fetch('/api/finance/treasury-colors', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ thresholds, reserveTargetCents }),
    });
    if (!response.ok) throw new Error('Les seuils de trésorerie n’ont pas pu être enregistrés.');
  },
  async saveManualBankBalance(cents: string, asOf: string, csrfToken: string): Promise<void> {
    const response = await fetch('/api/finance/bank-balance/fallback', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ cents, asOf }),
    });
    if (!response.ok) throw new Error('Le solde de secours n’a pas pu être enregistré.');
  },
  async saveRevenueSettings(
    revenueCents: string,
    breakEvenCents: string,
    csrfToken: string,
  ): Promise<void> {
    const response = await fetch('/api/finance/revenue-settings', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ revenueCents, breakEvenCents }),
    });
    if (!response.ok) throw new Error('Les objectifs n’ont pas pu être enregistrés.');
  },
  async read(month: string): Promise<FinancialOverview> {
    const response = await fetch(`/api/finance/overview?month=${encodeURIComponent(month)}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Le bilan financier est indisponible.');
    return response.json() as Promise<FinancialOverview>;
  },
  async saveWorkingCapitalAnnualGoal(targetCents: string, csrfToken: string): Promise<void> {
    const response = await fetch('/api/finance/working-capital-annual-goal', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ targetCents }),
    });
    if (!response.ok)
      throw new Error('L’objectif annuel du fond de roulement n’a pas pu être enregistré.');
  },
  async saveReserveTarget(
    targetCents: string,
    csrfToken: string,
    thresholds?: readonly number[],
  ): Promise<{ targetCents: string }> {
    const response = await fetch('/api/finance/reserve-target', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ targetCents, thresholds }),
    });
    if (!response.ok) throw new Error('Le seuil n’a pas pu être enregistré.');
    return response.json() as Promise<{ targetCents: string }>;
  },
  async saveGoal(
    kind: 'salary' | 'revenue',
    targetCents: string,
    csrfToken: string,
  ): Promise<{ targetCents: string }> {
    const response = await fetch(`/api/finance/goals/${kind}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ targetCents }),
    });
    if (!response.ok) throw new Error('L’objectif n’a pas pu être enregistré.');
    return response.json() as Promise<{ targetCents: string }>;
  },
};
